/* eslint-disable replo/no-tailwind-literal-color */

import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { StyleLibrary } from "schemas/generated/project";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import { useIsChatLoading } from "@v2/features/chat/store/ChatStoreContext";
import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { useCachedPageContent } from "@v2/features/page-renderer/hooks/page-cache";
import { usePage } from "@v2/features/page-renderer/hooks/usePage";
import { useViewportStore } from "@v2/features/page-renderer/store/viewportStore";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import useMeasure from "react-use-measure";
import { filterNulls } from "replo-utils/lib/array";
import { ReploError } from "schemas/errors";

import PagePreview from "./PagePreview";

type PageRendererProps = {
  pageRoute: string;
  overrideStyleLibrary: StyleLibrary | null;
  viewingVersionId: string | null;
  className?: string;
  /**
   * If true we will always render this page interactively from the WebContainer
   * (eventually). If false we will render a static version of the page from the
   * cache unless there is no cached version in which case we'll fall back to
   * the dynamically rendered one via WebContainer (which will be cached for
   * next time around).
   */
  isInteractive?: boolean;
  isScrollable?: boolean;
  previewThumbnail?: boolean;
  /**
   * Optional override for scale calculation only.
   * Locks the logical viewport used to compute scale, without changing wrapper sizing.
   */
  targetViewport?: EditorCanvas;
  /**
   * Optional overlay elements to render on top of the page content.
   * These will be positioned correctly relative to the scaled iframe content.
   */
  overlayElements?: React.ReactNode;
  /**
   * Optional callback ref to the root container element. Useful for scoping DOM queries
   * from parent components (e.g., selecting the correct iframe instance).
   */
  containerRef?: (node: HTMLDivElement | null) => void;
  /**
   * Visually hide scrollbars while still allowing scroll (template previews).
   */
  hideScrollbars?: boolean;
};

class PageRouteRequiredError extends ReploError {
  constructor(pageRoute: string) {
    super({
      message: "Page route not found",
      additionalData: { pageRoute },
    });
  }
}

function PageRenderer({
  pageRoute,
  className,
  isInteractive = false,
  isScrollable = true,
  previewThumbnail,
  targetViewport,
  overlayElements,
  containerRef,
  hideScrollbars,
  overrideStyleLibrary,
  viewingVersionId,
}: PageRendererProps) {
  if (!pageRoute) {
    throw new PageRouteRequiredError(pageRoute);
  }

  const { sendChatRequest, chatId } = useCurrentChatContext();

  const isChatLoading = useIsChatLoading(chatId);

  const projectId = useCurrentProjectId();

  const logEvent = useLogAnalytics();

  const { isLoading: isLoadingCachedPage, html: cachedPageHtml } =
    useCachedPageContent({ pageRoute, projectId });

  const {
    pageUrl,
    isLoading: isLoadingPage,
    isError,
    iframeKey,
    sandboxClientSideError,
  } = usePage({
    pageRoute,
    overrideStyleLibrary,
    viewingVersionId,
    // Note (Noah, 2025-10-23): If we're not supposed to be interactive,
    // and either we don't know if we're cached yet or we're for sure cached,
    // then we want to skip syncing to the sandbox to avoid polluting the sandbox
    // state in case other components (e.g. editor) are using it. In the future
    // it may make sense to have completely separate sandboxes for this case,
    // but for now this is a simple solution.
    forceSkipSandboxSync:
      !isInteractive && (isLoadingCachedPage || Boolean(cachedPageHtml)),
  });

  const { logs, loadedRoute } = useNextDevServer();
  const logsText = logs.map((log) => log.message).join("\n");

  const currentViewport = useViewportStore((state) => state.currentViewport);

  const [measureRef, containerDimensions] = useMeasure();

  // NOTE (Kurt, 2025-08-13): Since we are passing in our own ref to this component
  // and we are using useMeasure, we need to merge the internal measure ref with
  // the external containerRef callback.
  // NOTE (Kurt, 2025-11-05): We ignore the exhaustive dependencies warning because we want to prevent an infinite loop
  // where useMeasure returns a new ref function on every render causing setContainerRef to be recreated every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see docblock for explanation
  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      measureRef(node);
      if (containerRef) {
        containerRef(node);
      }
    },
    [containerRef],
  );

  // Get viewport-specific width from CANVAS_SIZES; allow optional override for scaling only
  const effectiveViewport: EditorCanvas = targetViewport ?? currentViewport;
  const viewportWidth = CANVAS_SIZES[effectiveViewport].defaultFrameWidth;

  // Calculate scale based on context and measured container size
  const scale = React.useMemo(() => {
    if (previewThumbnail) {
      return 0.25;
    }

    if (containerDimensions.width > 0) {
      // Scale viewport content to fit the container width exactly
      return containerDimensions.width / viewportWidth;
    }

    return 1; // Default to no scaling
  }, [containerDimensions.width, viewportWidth, previewThumbnail]);

  // NOTE (Yuxin, 2026-02-20, REPL-24207): Track when page loading exceeds 30 seconds
  // for monitoring purposes. We log a PostHog event if the page is still loading
  // after 30 seconds of continuous loading.
  // NOTE (Nick, 2026-02-23, REPL-24288): Only track timeouts when a page preview
  // is actually shown to the user (loadedRoute is set when IFRAME_READY fires).
  // This prevents firing during signup, onboarding, or before any page loads.
  const LOAD_TIMEOUT_MS = 30_000;
  React.useEffect(() => {
    if (!isLoadingPage || loadedRoute === null) {
      return;
    }

    const timeoutId = setTimeout(() => {
      logEvent("editor.preview.timeout", {
        pageRoute,
        loadingDurationMs: LOAD_TIMEOUT_MS,
      });
    }, LOAD_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoadingPage, loadedRoute, pageRoute, logEvent]);

  const handleFix = () => {
    // Note (Nick, 1/26/2026): Pass in Client Side error and dev server logs.
    // Currently the dev server does not have the runtime error, but will with next16 + mcp
    const errorContext = filterNulls([
      sandboxClientSideError?.message,
      logsText,
    ]).join("\n\n");
    void sendChatRequest({
      prompt: `Fix the following error (don't ask me any questions, just do it right away): ${errorContext}`,
      isHidden: true,
    });
  };

  return (
    <div
      ref={setContainerRef}
      className={twMerge("flex h-full w-full flex-col relative", className)}
    >
      <div
        className={twMerge("relative h-full w-full overflow-hidden", className)}
      >
        {isError && (
          <div className="absolute inset-0 bg-sheet z-1 overflow-auto">
            <div className="min-h-full flex items-center justify-center py-8">
              <Empty
                title="Error Loading Page"
                description={
                  isInteractive
                    ? "Ask Replo to fix it or restore an older version of this page."
                    : undefined
                }
                media={
                  <img
                    src="/images/chat/file-error.svg"
                    alt="Error Loading Page"
                  />
                }
              >
                {isInteractive && (
                  <Button
                    variant="secondary"
                    size="base"
                    onClick={handleFix}
                    loading={isChatLoading}
                  >
                    Fix it for me
                  </Button>
                )}
              </Empty>
            </div>
          </div>
        )}

        <div
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: targetViewport ? `${viewportWidth}px` : `${100 / scale}%`,
            height:
              targetViewport && containerDimensions.height > 0
                ? `${containerDimensions.height / scale}px`
                : `${100 / scale}%`,
            overflow: "hidden",
          }}
          className="flex h-full flex-col"
        >
          <div className="flex h-full w-full flex-col">
            <div className="relative flex h-full w-full flex-col items-center justify-start">
              <PagePreview
                cachedPageHtml={cachedPageHtml}
                isLoadingPage={isLoadingPage}
                isInteractive={isInteractive}
                isScrollable={isScrollable}
                hideScrollbars={hideScrollbars}
              />
              {/* NOTE (Gabe 2025-07-14): This is rendered even if isLoadingPage is true because this iframe is
                  what requests the page from the web container, causing it to transition to loaded. Also if there
                  is no cachedPageHtml, we render the iframe to request the page from the web container (which will
                  be cached next time). */}
              {(isInteractive || (!isLoadingCachedPage && !cachedPageHtml)) && (
                <iframe
                  src={pageUrl}
                  key={iframeKey}
                  className={twMerge(
                    "h-full w-full border-0",
                    isLoadingPage && "opacity-0 pointer-events-none",
                  )}
                  // NOTE (Ben 2025-07-17): This is needed to prevent the iframe from being included in window
                  // history and messing up navigation.
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          </div>
          {/* Overlay Elements - positioned relative to scaled content */}
          {overlayElements && (
            <div className="absolute inset-0 pointer-events-none z-[99999]">
              {overlayElements}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageRenderer;
